import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { Truck, Phone, Link as LinkIcon } from '../../lib/lucide-proxy';
import { supabase } from '../../config/supabase';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/ui/LoadingSpinner';

const Settings = () => {
  const { profile, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [breweryData, setBreweryData] = useState({
    name: '',
    address: '',
    phone: '',
    delivery_config: {
      type: 'platform',
      whatsapp: '',
      link: '',
      allow_pickup: false
    }
  });

  useEffect(() => {
    if (!authLoading) {
      if (profile?.brewery?.id) {
        fetchBreweryData();
      } else {
        setLoading(false);
      }
    }
  }, [profile, authLoading]);

  const fetchBreweryData = async () => {
    try {
      const { data, error } = await supabase
        .from('breweries')
        .select('*')
        .eq('id', profile.brewery.id)
        .single();

      if (error) throw error;

      setBreweryData({
        name: data.name || '',
        address: data.address || '',
        phone: data.phone || '',
        delivery_config: {
          type: 'platform',
          whatsapp: '',
          link: '',
          allow_pickup: false,
          ...data.delivery_config
        }
      });
    } catch (error) {
      console.error('Error fetching settings:', error);
      toast.error('Erro ao carregar configurações');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setBreweryData(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleConfigChange = (field, value) => {
    setBreweryData(prev => ({
      ...prev,
      delivery_config: { ...prev.delivery_config, [field]: value }
    }));
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (!profile?.brewery?.id) {
        toast.error("Nenhuma cervejaria vinculada para salvar.");
        return;
      }

      const { error } = await supabase
        .from('breweries')
        .update({
          name: breweryData.name,
          address: breweryData.address,
          phone: breweryData.phone,
          delivery_config: breweryData.delivery_config
        })
        .eq('id', profile.brewery.id);

      if (error) throw error;
      toast.success('Configurações salvas com sucesso!');
    } catch (error) {
      console.error('Error saving settings:', error);
      toast.error('Erro ao salvar configurações');
    } finally {
      setSaving(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const isAdmin = profile?.role === 'admin';

  return (
    <>
      <Helmet>
        <title>Configurações - Cerveja Delivery</title>
      </Helmet>

      <div className="space-y-8">
        <div>
          <h2 className="text-2xl font-bold text-dark-900">Configurações da Cervejaria</h2>
          <p className="text-dark-600">Gerencie seus dados e opções de entrega.</p>
        </div>

        {!profile?.brewery && !isAdmin ? (
           <div className="bg-yellow-50 p-6 rounded-xl border border-yellow-200 text-center">
              <p className="text-yellow-800 font-bold">Nenhuma Cervejaria Vinculada</p>
              <p className="text-yellow-700 text-sm mt-2">Entre em contato com o suporte ou crie uma nova conta de parceiro.</p>
           </div>
        ) : (
          <form onSubmit={handleSave} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* General Data */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-fit">
              <h3 className="text-lg font-bold text-dark-900 mb-4">Dados Gerais</h3>
              <div className="space-y-4">
                <Input label="Nome da Cervejaria" name="name" value={breweryData.name} onChange={handleChange} />
                <Input label="Endereço" name="address" value={breweryData.address} onChange={handleChange} />
                <Input label="Telefone Comercial" name="phone" value={breweryData.phone} onChange={handleChange} />
              </div>
            </div>

            {/* Delivery Configuration */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 h-fit">
              <div className="flex items-center space-x-2 mb-6">
                <Truck className="w-5 h-5 text-primary-500" />
                <h3 className="text-lg font-bold text-dark-900">Configurações de Entrega</h3>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Método de Venda</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <button
                      type="button"
                      onClick={() => handleConfigChange('type', 'platform')}
                      className={`p-3 text-left rounded-lg border-2 transition-all ${breweryData.delivery_config.type === 'platform' ? 'border-primary-500 bg-primary-50 text-primary-900' : 'border-gray-200 hover:border-primary-300'}`}
                    >
                      <div className="font-semibold text-sm">Plataforma</div>
                      <div className="text-xs opacity-80">Carrinho e gestão completa</div>
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => handleConfigChange('type', 'whatsapp')}
                      className={`p-3 text-left rounded-lg border-2 transition-all ${breweryData.delivery_config.type === 'whatsapp' ? 'border-green-500 bg-green-50 text-green-900' : 'border-gray-200 hover:border-green-300'}`}
                    >
                      <div className="font-semibold text-sm">WhatsApp</div>
                      <div className="text-xs opacity-80">Pedido enviado por msg</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleConfigChange('type', 'external')}
                      className={`p-3 text-left rounded-lg border-2 transition-all ${breweryData.delivery_config.type === 'external' ? 'border-blue-500 bg-blue-50 text-blue-900' : 'border-gray-200 hover:border-blue-300'}`}
                    >
                      <div className="font-semibold text-sm">Link Externo</div>
                      <div className="text-xs opacity-80">iFood, Rappi, Site próprio</div>
                    </button>

                    <button
                      type="button"
                      onClick={() => handleConfigChange('type', 'none')}
                      className={`p-3 text-left rounded-lg border-2 transition-all ${breweryData.delivery_config.type === 'none' ? 'border-gray-400 bg-gray-100 text-gray-900' : 'border-gray-200 hover:border-gray-400'}`}
                    >
                      <div className="font-semibold text-sm">Apenas Menu</div>
                      <div className="text-xs opacity-80">Visualização, sem vendas</div>
                    </button>
                  </div>
                </div>

                {breweryData.delivery_config.type === 'whatsapp' && (
                  <div className="animate-fade-in-up">
                    <Input 
                      label="Número do WhatsApp (com DDD)" 
                      placeholder="Ex: 11999999999" 
                      value={breweryData.delivery_config.whatsapp} 
                      onChange={(e) => handleConfigChange('whatsapp', e.target.value)}
                      icon={Phone}
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      O pedido será montado no carrinho e enviado como mensagem pronta para este número.
                    </p>
                  </div>
                )}

                {breweryData.delivery_config.type === 'external' && (
                  <div className="animate-fade-in-up">
                    <Input 
                      label="Link de Delivery" 
                      placeholder="https://..." 
                      value={breweryData.delivery_config.link} 
                      onChange={(e) => handleConfigChange('link', e.target.value)}
                      icon={LinkIcon}
                    />
                  </div>
                )}

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="allow_pickup"
                    checked={breweryData.delivery_config.allow_pickup}
                    onChange={(e) => handleConfigChange('allow_pickup', e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
                  />
                  <label htmlFor="allow_pickup" className="ml-2 text-sm text-gray-700">
                    Permitir retirada no local (Pickup)
                  </label>
                </div>
              </div>
            </div>
          </form>
        )}
        
        {profile?.brewery && (
          <div className="max-w-2xl">
              <Button variant="primary" onClick={handleSave} loading={saving} className="w-full sm:w-auto">
                Salvar Alterações
              </Button>
          </div>
        )}
      </div>
    </>
  );
};

export default Settings;